'use client';

import cx from 'classix';
import { formatDistance } from 'date-fns';
import Image from 'next/image';
import {
  useLocale,
  useTranslations,
} from 'next-intl';
import {
  type MouseEvent,
  useState,
} from 'react';

import ListingDetailAllPhotos
  from '@/features/listing/components/listing-detailed/listing-detail-gallery/listing-detail-all-photos/listing-detail-all-photos';
import { LISTINGS_TYPES } from '@/features/listing/constants/listing-constants';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import {
  type Listing,
  type ListingType,
} from '@/features/listing/types/listing-types';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { useRouter } from '@/i18n/routing';
import getDateFnsLocale from '@/libs/date-fns/get-date-fns-locale';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { type ImageThumbnail } from '@/shared/types/image-thumbnail-types';

import styles from './listing-detail-gallery.module.scss';

export interface ListingDetailGalleryProps {
  images: ImageThumbnail[];
  alt: string;
  isNew: Listing['isNew'];
  isBookmarked: Listing['isBookmarked'];
  listingRefreshedDate: Listing['refreshed'];
  listingId: Listing['id'];
  listingType: ListingType;
}

export default function ListingDetailGallery(props: ListingDetailGalleryProps) {
  const {
    alt,
    images,
    isBookmarked,
    isNew,
    listingId,
    listingRefreshedDate,
    listingType,
  } = props;

  const listingDetailTranslations = useTranslations('listing.detail');
  const listingLabelsTranslations = useTranslations('listing.labels');

  const locale = useLocale();
  const router = useRouter();

  const [isAllPhotosShow,
    setIsAllPhotosShow] = useState(false);

  const [isListingBookmarked,
    setIsListingBookmarked] = useState<boolean>(isBookmarked);

  const mainImage = images[0];
  const externalImages = images.slice(1, 5);

  const relativeRefreshDate = formatDistance(
    listingRefreshedDate,
    new Date(),
    {
      addSuffix: true,
      locale: getDateFnsLocale(locale),
    },
  );

  const handleShowAllPhotos = () => {
    setIsAllPhotosShow(true);
  };

  const handleHideAllPhotos = () => {
    setIsAllPhotosShow(false);
  };

  const handleBookmarkClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const loggedUser = getUserBearerCookie();

    if (loggedUser) {
      const currentListingTypeId = LISTINGS_TYPES[listingType];

      if (isListingBookmarked) {
        setIsListingBookmarked(false);

        deleteListingBookmark(listingId, currentListingTypeId)
          .catch(() => null);
      } else {
        setIsListingBookmarked(true);

        createListingBookmark(listingId, currentListingTypeId)
          .catch(() => null);
      }
    } else {
      // TODO: [auth-redirect]

      router.push(ROUTER_LINKS.phoneAuth);
    }
  };

  return (
    <>
      {images.length > 0 && (
        <div
          className={cx(
            styles.listingDetailGallery,
            externalImages.length >= 4 && styles.listingDetailGallery__five,
            externalImages.length === 3 && styles.listingDetailGallery__four,
            externalImages.length === 2 && styles.listingDetailGallery__three,
            externalImages.length === 1 && styles.listingDetailGallery__two,
            externalImages.length === 0 && styles.listingDetailGallery__one,
          )}
        >
          {isNew && (
            <div className={styles.listingDetailGallery_isNew}>
              {listingLabelsTranslations('new')}
            </div>
          )}

          <div className={styles.listingDetailGallery_relativeDate}>
            {relativeRefreshDate}
          </div>

          {externalImages.length > 0 && (
            <button onClick={handleShowAllPhotos} className={styles.listingDetailGallery_showAllButton}>
              {listingDetailTranslations('show-all-photos')}
            </button>
          )}

          <div className={styles.listingDetailGallery__mainImage}>
            <Image
              src={mainImage.path}
              alt={alt}
                // width={372}
                // height={279}
              quality={75}
              fill
              unoptimized
            />
          </div>

          {externalImages.map((image, index) => (
            <div key={image.id} className={styles[`listingDetailGallery__extra${(index + 1).toString()}`]}>
              <Image
                src={image.path}
                alt={alt}
                // width={372}
                // height={279}
                quality={75}
                fill
                unoptimized
              />
            </div>
          ))}
        </div>
      )}

      {isAllPhotosShow && (
        <ListingDetailAllPhotos
          images={images}
          alt={alt}
          onClose={handleHideAllPhotos}
          isBookmarked={isListingBookmarked}
          isNew={isNew}
          listingId={listingId}
          listingRefreshedDate={listingRefreshedDate}
          onBookmarkClick={handleBookmarkClick}
        />
      )}
    </>
  );
}
